export function mean(xs: readonly number[]): number {
  if (xs.length === 0) return Number.NaN;
  let sum = 0;
  for (const x of xs) sum += x;
  return sum / xs.length;
}

export function median(xs: readonly number[]): number {
  if (xs.length === 0) return Number.NaN;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = (sorted.length - 1) / 2;
  const lo = Math.floor(mid);
  const hi = Math.ceil(mid);
  return (sorted[lo]! + sorted[hi]!) / 2;
}

/**
 * Quartiles via R's type 7 / Excel's `QUARTILE` (linear interpolation between
 * order statistics). Matches the quartile convention used by Observable Plot's
 * box-plot rendering, so the on-card 5-number-summary agrees with the visual.
 */
export function quartiles(xs: readonly number[]): { q1: number; q2: number; q3: number } {
  if (xs.length === 0) return { q1: Number.NaN, q2: Number.NaN, q3: Number.NaN };
  const sorted = [...xs].sort((a, b) => a - b);
  return {
    q1: quantileSorted(sorted, 0.25),
    q2: quantileSorted(sorted, 0.5),
    q3: quantileSorted(sorted, 0.75),
  };
}

export function range(xs: readonly number[]): { min: number; max: number; span: number } {
  if (xs.length === 0) return { min: Number.NaN, max: Number.NaN, span: Number.NaN };
  let min = xs[0]!;
  let max = xs[0]!;
  for (const x of xs) {
    if (x < min) min = x;
    if (x > max) max = x;
  }
  return { min, max, span: max - min };
}

export function iqr(xs: readonly number[]): number {
  const q = quartiles(xs);
  return q.q3 - q.q1;
}

export interface FiveNumberSummary {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

export function fiveNumberSummary(xs: readonly number[]): FiveNumberSummary {
  const r = range(xs);
  const q = quartiles(xs);
  return { min: r.min, q1: q.q1, median: q.q2, q3: q.q3, max: r.max };
}

/** Sample standard deviation (n−1). NaN for n < 2. */
export function stdDev(values: readonly number[]): number {
  if (values.length < 2) return Number.NaN;
  const m = mean(values);
  let sumSq = 0;
  for (const v of values) sumSq += (v - m) ** 2;
  return Math.sqrt(sumSq / (values.length - 1));
}

function quantileSorted(sorted: readonly number[], p: number): number {
  const h = (sorted.length - 1) * p;
  const lo = Math.floor(h);
  const hi = Math.ceil(h);
  if (lo === hi) return sorted[lo]!;
  return sorted[lo]! + (h - lo) * (sorted[hi]! - sorted[lo]!);
}
